import { useEffect, useMemo, useRef, useState } from "react";
import { Download, Share2, Sparkles, Check, X } from "lucide-react";
import { SearchPick } from "@/components/SearchPick";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { W, H, SQ, loadFonts, drawDreams, drawBestOf, drawYear, drawTop10, drawDreamsSquare, drawBestOfSquare, drawYearSquare, drawTop10Square, drawJoin, drawJoinSquare } from "@/lib/story";
import { useQueryClient } from "@tanstack/react-query";
import { StoryStats } from "./StoryStats";
import { HowToPost } from "./HowToPost";
import { useInviteText } from "@/components/InviteMessage";
import { api, shortUrl, withSource } from "@/lib/api";
import { yearsAvailable } from "@/lib/yearReview";
import { useAuth } from "@/hooks/useAuth";

const TEMPLATES = [["dreams", "My bucketlist"], ["top10", "My top 10"], ["best", "My top 5 ever"], ["year", "Year in review"]];
const DRAWS = {
  story: { dreams: drawDreams, top10: drawTop10, best: drawBestOf, year: drawYear, join: drawJoin },
  square: { dreams: drawDreamsSquare, top10: drawTop10Square, best: drawBestOfSquare, year: drawYearSquare, join: drawJoinSquare },
};

const StoryCanvas = ({ geo, data, template, format, title, goals, wish, year, link, canvasRef }) => {
  useEffect(() => {
    let alive = true;
    loadFonts().then(() => {
      if (!alive || !canvasRef.current) return;
      const ctx = canvasRef.current.getContext("2d");
      DRAWS[format][template](ctx, geo, data, { title, goals, wish, year, link });
    });
    return () => { alive = false; };
  }, [geo, data, template, format, title, goals, wish, year, link, canvasRef]);
  const sq = format === "square";
  return <canvas key={format} ref={canvasRef} width={W} height={sq ? SQ : H} className={`${sq ? "w-[300px] sm:w-[320px] aspect-square" : "w-[270px] sm:w-[300px] aspect-[9/16]"} rounded-2xl shadow-2xl ring-1 ring-white/10`} data-testid="story-canvas" />;
};

const WishPicker = ({ names, wishlist, picked, setPicked, b }) => {
  const rank = Object.fromEntries(wishlist.map((w, i) => [w.country, i + 1]));
  const options = [...names].filter((n) => !picked.includes(n)).sort((a, c) => (rank[a] || 999) - (rank[c] || 999))
    .map((n) => ({ key: n, label: n, ...(rank[n] ? { hint: `#${rank[n]} on list`, hintColor: "#F59E0B" } : { hint: "Will be added", hintColor: "#64748B" }) }));
  const pick = (o) => {
    if (picked.length >= 5) return;
    if (!rank[o.label]) b.addWish(o.label);
    setPicked([...picked, o.label]);
  };
  return (
    <div>
      <p className="eyebrow !text-amber-300/80 mb-2">Top 5 places · {picked.length}/5</p>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {picked.map((n, i) => (
          <span key={n} className="flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-amber-400/15 text-amber-100 text-xs" data-testid={`story-wish-chip-${i + 1}`}>
            <span className="font-mono text-amber-400">{i + 1}</span> {n}
            <button onClick={() => setPicked(picked.filter((x) => x !== n))} className="p-0.5 hover:text-white" aria-label={`Remove ${n}`} data-testid={`story-wish-remove-${i + 1}`}><X size={11} /></button>
          </span>
        ))}
      </div>
      {picked.length < 5 && <SearchPick options={options} onPick={pick} placeholder="Search a country…" testId="story-wish-search" accent="#F59E0B" />}
      {picked.length < 5 && <p className="text-[11px] text-slate-500 mt-1.5">A country that isn't on your list yet also gets added to Want to go.</p>}
    </div>
  );
};

const GoalSearch = ({ goals, picked, setPicked, b, setPending }) => (
  <SearchPick options={goals.filter((g) => !picked.includes(g.id)).map((g) => ({ key: g.id, label: g.title, hint: g.done ? "Done" : "" }))}
    onPick={(o) => picked.length < 5 && setPicked([...picked, o.key])}
    onCreate={(q) => { if (picked.length < 5) { b.addGoal({ title: q }); setPending(q); } }}
    createLabel={(q) => `Add "${q}" to my list`} placeholder="Search or add a dream…" testId="story-goal-search" accent="#FB7185" />
);

const GoalPicker = ({ goals, picked, setPicked, b, setPending }) => (
  <div>
    <p className="eyebrow !text-rose-300/80 mb-2">Pick up to 5 dreams · {picked.length}/5</p>
    {picked.length < 5 && <div className="mb-2"><GoalSearch goals={goals} picked={picked} setPicked={setPicked} b={b} setPending={setPending} /></div>}
    <div className="flex flex-col gap-1 max-h-48 overflow-y-auto pr-1">
      {goals.map((g) => {
        const on = picked.includes(g.id);
        return (
          <button key={g.id} onClick={() => setPicked(on ? picked.filter((x) => x !== g.id) : picked.length < 5 ? [...picked, g.id] : picked)}
            className={`flex items-center gap-2 text-left text-sm px-2 py-1.5 rounded-lg transition-colors ${on ? "bg-rose-400/15 text-rose-100" : "text-slate-400 hover:bg-white/5"}`} data-testid={`story-goal-${g.id}`}>
            <span className={`w-4 h-4 rounded grid place-items-center border ${on ? "bg-rose-400 border-rose-400 text-[#1f0a0e]" : "border-white/20"}`}>{on && <Check size={11} />}</span>
            <span className="truncate">{g.title}</span>
          </button>
        );
      })}
      {goals.length === 0 && <p className="text-xs text-slate-500">Add some "Before I die" goals first.</p>}
    </div>
  </div>
);

export const StoryDialog = ({ geo, data, b, children }) => {
  const { user } = useAuth();
  const canvasRef = useRef(null);
  const inviteText = useInviteText();
  const [template, setTemplate] = useState("dreams");
  const [customTitle, setTitle] = useState(null);
  const first = user ? `${user.name.split(" ")[0]}'s` : "My";
  const years = useMemo(() => yearsAvailable(data), [data]);
  const [year, setYear] = useState(years[0]);
  const title = customTitle ?? ({ dreams: `${first} bucketlist`, top10: `${first} next adventures`, best: `${first} best travels`, year: `${first} year in travel` })[template];
  const freshGoals = () => data.goals.filter((g) => !g.done).slice(0, 5).map((g) => g.id);
  const freshWish = () => data.wishlist.slice(0, 5).map((w) => w.country);
  const [picked, setPicked] = useState(freshGoals);
  const [wish, setWish] = useState(freshWish);
  const [open, setOpen] = useState(false);
  const onOpenChange = (o) => {
    if (o) { setPicked(freshGoals()); setWish(freshWish()); setTitle(null); }
    setOpen(o);
  };
  // Drop picks whose goal was deleted (or restored with a new id) while the dialog is open
  useEffect(() => {
    setPicked((p) => (p.every((id) => data.goals.some((g) => g.id === id)) ? p : p.filter((id) => data.goals.some((g) => g.id === id))));
  }, [data.goals]);
  const goals = useMemo(() => picked.map((id) => data.goals.find((g) => g.id === id)).filter(Boolean), [picked, data.goals]);
  const [pending, setPending] = useState(null);
  const [format, setFormat] = useState("story");
  const [codes, setCodes] = useState({});
  const qc = useQueryClient();
  const [link, setLink] = useState("");
  const tpl = template;
  // Each template/format gets its own tracked link the first time it's shared, downloaded or copied
  const trackedLink = async () => {
    const key = `${tpl}-${format}`;
    let code = codes[key];
    if (!code) {
      code = (await api.createSource({ template: tpl, format }).catch(() => ({}))).code;
      if (code) { setCodes((c) => ({ ...c, [key]: code })); qc.invalidateQueries({ queryKey: ["source-stats"] }); }
    }
    return withSource(link, code);
  };
  useEffect(() => {
    if (!link) api.shareInfo().then((i) => setLink(shortUrl(i.handle))).catch(() => null);
  }, [template, link]);
  useEffect(() => {
    const g = pending && data.goals.find((x) => x.title === pending);
    if (g) { setPicked((p) => (p.includes(g.id) || p.length >= 5 ? p : [...p, g.id])); setPending(null); }
  }, [pending, data.goals]);

  const toFile = () => new Promise((res) => {
    const ctx = canvasRef.current.getContext("2d");
    DRAWS[format][tpl](ctx, geo, data, { title, goals, wish, year, link, withGrain: true });
    canvasRef.current.toBlob((b) => res(new File([b], `my-bucketlist-${format === "square" ? "post" : "story"}.png`, { type: "image/png" })), "image/png");
  });
  const download = async () => {
    await copyLink();
    const file = await toFile();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(file); a.download = file.name; a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Story saved — add it to your Instagram or Facebook story");
  };
  // Copy the sticker link in the same tap so posting is just "paste into a Link sticker"
  const copyLink = () => {
    if (!link) return Promise.resolve("");
    const p = trackedLink();
    const write = window.ClipboardItem && navigator.clipboard?.write
      ? navigator.clipboard.write([new window.ClipboardItem({ "text/plain": p.then((t) => new Blob([t], { type: "text/plain" })) })])
      : p.then((t) => navigator.clipboard?.writeText(t));
    write.then(() => toast.success("Link copied — add it as a Link sticker in your story"), () => null);
    return p;
  };
  const share = async () => {
    const tracked = await copyLink();
    const file = await toFile();
    if (navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], title, ...(tracked ? { text: inviteText(tracked) } : {}) }).catch(() => null);
    } else {
      toast.message("Sharing isn't supported in this browser — downloading instead");
      download();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="story-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Sparkles size={18} className="text-emerald-400" /> Create a story</DialogTitle>
          <DialogDescription>{format === "square" ? "Make a square picture of your bucket list for an Instagram or Facebook feed post." : "Make a picture of your bucket list for your Instagram or Facebook story."} Just follow the 4 steps.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col sm:flex-row gap-6">
          <div className="mx-auto sm:mx-0 shrink-0">
            <StoryCanvas geo={geo} data={data} template={tpl} format={format} title={title} goals={goals} wish={wish} year={year} link={link} canvasRef={canvasRef} />
          </div>
          <div className="flex-1 space-y-5 min-w-0">
            <div className="inline-flex rounded-full border border-white/10 p-0.5" data-testid="story-format-toggle">
              {[["story", "Story 9:16"], ["square", "Square 1:1"]].map(([k, l]) => (
                <button key={k} onClick={() => setFormat(k)} data-testid={`story-format-${k}`}
                  className={`px-3 py-1 rounded-full text-xs transition-colors ${format === k ? "bg-white text-[#0B0F17]" : "text-slate-400 hover:text-white"}`}>{l}</button>
              ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {TEMPLATES.map(([k, l]) => (
                <button key={k} onClick={() => setTemplate(k)} data-testid={`story-template-${k}`}
                  className={`py-2 rounded-lg border text-sm transition-colors ${template === k ? "border-emerald-400 text-emerald-200 bg-emerald-400/10" : "border-white/10 text-slate-400 hover:text-white"}`}>{l}</button>
              ))}
            </div>
            <label className="block text-xs text-slate-400">Title
              <Input value={title} maxLength={40} onChange={(e) => setTitle(e.target.value)} className="mt-1" data-testid="story-title-input" />
            </label>
            {template === "dreams" ? (
              <>
                <WishPicker names={geo.names} wishlist={data.wishlist} picked={wish} setPicked={setWish} b={b} />
                <GoalPicker goals={data.goals} picked={picked} setPicked={setPicked} b={b} setPending={setPending} />
              </>
            ) : template === "top10" ? (
              <div className="space-y-3" data-testid="story-top10-info">
                <p className="text-xs text-slate-500">Shows the first 10 countries on your Want to go list — reorder them on your page.</p>
                {link && (
                  <Button variant="outline" size="sm" className="bg-transparent" data-testid="story-copy-invite-button"
                    onClick={async () => navigator.clipboard.writeText(inviteText(await trackedLink())).then(() => toast.success("Invite text copied — paste it with your story"), () => null)}>Copy invite text with link</Button>
                )}
              </div>
            ) : template === "best" ? (
              <p className="text-xs text-slate-500">Shows your "Top 5, ever" — edit it on your page.</p>
            ) : (
              <div>
                <p className="eyebrow !text-sky-300/80 mb-2">Year</p>
                <div className="flex flex-wrap gap-2">
                  {years.map((y) => (
                    <button key={y} onClick={() => setYear(y)} data-testid={`story-year-${y}`}
                      className={`px-3 py-1 rounded-full font-mono text-xs border transition-colors ${year === y ? "bg-sky-400 text-[#06121c] border-sky-400" : "border-white/10 text-slate-400 hover:text-white"}`}>{y}</button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-3">Counts countries and cities from your visit dates (Been there → calendar icon) and dreams by the day you ticked them off.</p>
              </div>
            )}
            {link && <HowToPost square={format === "square"} onCopy={async () => { const t = await trackedLink(); navigator.clipboard.writeText(t).then(() => toast.success("Link copied — paste it into a Link sticker"), () => null); }} />}
            <div className="flex gap-2">
              <Button onClick={share} className="flex-1 bg-gradient-to-r from-emerald-400 to-sky-400 text-[#04120c] hover:opacity-90" data-testid="story-share-button"><Share2 size={14} /> Share</Button>
              <Button onClick={download} variant="outline" className="flex-1 bg-transparent" data-testid="story-download-button"><Download size={14} /> Download</Button>
            </div>
            <StoryStats open={open} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
